import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import type { Tone } from "@/tokens";
import { warningSurfaceTextStyle } from "../warningVisual";

export type TipProps = HTMLAttributes<HTMLSpanElement> & {
  tone?: Tone;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const bgText = (bg: string, text: string): CSSObject => ({
  "--tw-bg-opacity": "1",
  backgroundColor: `hsl(var(--bfrs-color-${bg}) / var(--tw-bg-opacity, 1))`,
  "--tw-text-opacity": "1",
  color: `hsl(var(--bfrs-color-${text}) / var(--tw-text-opacity, 1))`
});

const tipToneStyles: Record<Tone, CSSObject> = {
  neutral: bgText("surface-muted", "text-secondary"),
  primary: bgText("primary-soft", "primary"),
  brand: bgText("primary-soft", "primary"),
  success: bgText("success-soft", "success-contrast"),
  warning: {},
  danger: bgText("danger-soft", "danger-contrast"),
  info: bgText("info-soft", "info-contrast")
};

const TipRoot = styled("span", {
  label: "AgenticTip-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { tone: Tone } }>(({ ownerState }) => [
  {
    display: "inline-flex",
    minHeight: "1.25rem",
    alignItems: "center",
    borderRadius: "var(--bfrs-radius-pill)",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: "1.25rem"
  },
  tipToneStyles[ownerState.tone]
]);

export const Tip = forwardRef<HTMLSpanElement, TipProps>(({ className, tone = "primary", style, ...props }, ref) => (
  <TipRoot
    ref={ref}
    ownerState={{ tone }}
    className={className}
    style={{ ...(tone === "warning" ? warningSurfaceTextStyle : undefined), ...style }}
    {...props}
  />
));

Tip.displayName = "Tip";
